import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TERM_GUIDES } from '../data/guides';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight, FileText, Video, HelpCircle, BookOpen, Clock, ChevronDown, Pause, Play, RotateCcw } from 'lucide-react';
import SEO from '../components/SEO';
import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';
import { shortDate } from '../lib/siteDates';

// The words of these pages live in public/locales/<lang>/resources.json under the page's
// key (focus_timer for the timer), its items under items.<key> (title, desc, body[], cta).
// This keeps what is not text: icons, dates and where each item links.
const resourceContent = {
  blog: {
    icon: FileText,
    items: [
      { key: 'kanban', date: '2026-03-15', cta: '/features/projects' },
      { key: 'habits', date: '2026-03-10', cta: '/features/habits' },
      { key: 'one_line', date: '2026-03-05', cta: '/features/task-management' },
      ...(GOOGLE_APP_INTEGRATIONS ? [{ key: 'gmail', date: '2026-03-01', cta: '/features/gmail-integration' }] : []),
    ],
  },
  help: {
    icon: HelpCircle,
    items: [
      { key: 'getting_started', cta: '/register' },
      { key: 'tasks_bots', cta: '/ai-bots' },
      { key: 'teams', cta: '/features/team-collaboration' },
      { key: 'integrations', cta: '/integrations' },
    ],
  },
  // A 'Webinars & Demos' section used to live here advertising a recurring
  // 'Weekly Product Demo, Every Thursday 2pm ET', a 'Deep Work Masterclass'
  // with a downloadable workbook, a rollout session and a Founders AMA, all
  // with 'Recording available'. No such sessions are run and no recordings
  // exist, so people were invited to register for events that never happen.
  // The /webinars route now 404s until there is something real to announce.
  // A 'Case Studies' section used to live here with four customer stories:
  // TechCorp, StartupX and two unnamed teams, complete with metrics such as
  // "Manager NPS went from -12 to +47" and "attrition dropped from 22% to 4%".
  // None of those customers or measurements exist. The SEO description even
  // called them "Real results from real organizations". All of it is removed;
  // the /case-studies route now redirects to /resources.
  glossary: {
    icon: BookOpen,
    items: [{ key: 'deep_work' }, { key: 'time_blocking' }, { key: 'context_switching' }, { key: 'maker_schedule' }],
  },
  'focus-timer': {
    icon: Clock,
    items: [],
  },
};

const asList = (v) => (Array.isArray(v) ? v : []);

const FOCUS = 25 * 60;
const BREAK = 5 * 60;

const PomodoroTimer = () => {
  const { t } = useTranslation('landing');
  const [phase, setPhase] = useState('focus'); // 'focus' | 'break'
  const [secondsLeft, setSecondsLeft] = useState(FOCUS);
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  useEffect(() => {
    if (secondsLeft !== 0) return;
    setRunning(false);
    if (phase === 'focus') {
      setCompleted((n) => n + 1);
      setPhase('break');
      setSecondsLeft(BREAK);
    } else {
      setPhase('focus');
      setSecondsLeft(FOCUS);
    }
  }, [secondsLeft, phase]);

  const reset = () => {
    setRunning(false);
    setPhase('focus');
    setSecondsLeft(FOCUS);
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className="bg-white/5 rounded-2xl border border-gray-800 p-8 sm:p-12 mb-8">
      <div className="text-7xl sm:text-8xl font-bold text-amber-400 mb-2 tabular-nums">{mm}:{ss}</div>
      <p className="text-gray-400 mb-8">{phase === 'focus' ? t('resources.timer.focus_session') : t('resources.timer.break_session')}</p>
      <div className="flex justify-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setRunning((r) => !r)}
          className="bg-amber-500 text-black px-8 py-3 rounded-lg font-medium hover:bg-amber-400 transition-colors inline-flex items-center gap-2"
        >
          {running ? <><Pause className="w-4 h-4" /> {t('resources.timer.pause')}</> : <><Play className="w-4 h-4" /> {t('resources.timer.start')}</>}
        </button>
        <button
          type="button"
          onClick={reset}
          className="border border-gray-700 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors inline-flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          {t('resources.timer.reset')}
        </button>
      </div>
      <p className="text-gray-500 text-sm mt-6">{t('resources.timer.sessions_today')}: <span className="text-white font-medium">{completed}</span></p>
    </div>
  );
};

const ResourcePage = () => {
  const { t, i18n } = useTranslation(['landing', 'resources']);
  const { resourceId } = useParams();
  const location = useLocation();
  const [openItem, setOpenItem] = useState(null);

  let activeResource = resourceId;
  if (!activeResource) {
    const path = location.pathname;
    if (path === '/blog') activeResource = 'blog';
    else if (path === '/help') activeResource = 'help';
    else if (path === '/glossary') activeResource = 'glossary';
    else if (path === '/tools/focus-timer') activeResource = 'focus-timer';
    else activeResource = 'blog';
  }

  if (!resourceContent[activeResource]) activeResource = 'blog';
  const config = resourceContent[activeResource];
  const Icon = config.icon;
  const key = activeResource.replace(/-/g, '_');
  const c = t(`resources:${key}`, { returnObjects: true });
  const text = c && typeof c === 'object' ? c : {};
  const lang = i18n.resolvedLanguage || 'en';
  const resource = {
    title: text.title,
    subtitle: text.subtitle,
    items: config.items.map((item) => {
      const words = text.items?.[item.key] || {};
      return {
        title: words.title,
        desc: words.desc,
        date: item.date && shortDate(item.date, lang),
        body: asList(words.body),
        cta: item.cta && { label: words.cta, to: item.cta },
      };
    }),
  };

  const CANONICAL = { blog: '/blog', help: '/help', glossary: '/glossary', 'focus-timer': '/tools/focus-timer' };
  const seo = { title: text.seo_title, description: text.seo_description, canonical: CANONICAL[activeResource] };

  if (activeResource === 'focus-timer') {
    return (
      <div className="min-h-screen bg-[#0a0a0b]">
        <SEO title={seo.title} description={seo.description} canonical={seo.canonical} />
        <Navbar />
        <main className="pt-20 pb-24">
          <section className="max-w-2xl mx-auto px-4 py-12 sm:py-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('resources.timer.title')}</h1>
            <p className="text-gray-400 mb-8">{t('resources.timer.body')}</p>
            <PomodoroTimer />
            <p className="text-gray-500 text-sm">{t('resources.timer.upsell_prefix')} <Link to="/register" className="text-amber-400 hover:text-amber-300">{t('resources.timer.upsell_link')}</Link></p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO title={seo.title} description={seo.description} canonical={seo.canonical} />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">{resource.title}</h1>
              <p className="text-gray-400 text-sm sm:text-base">{resource.subtitle}</p>
            </div>
          </div>
        </section>

        {/* AI terms, each with its own page (data/guides.js) */}
        {activeResource === 'glossary' && (
          <section className="max-w-4xl mx-auto px-4 pb-10">
            <h2 className="text-xl font-bold text-white mb-4">{t('resources.glossary_ai')}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {TERM_GUIDES.map((g) => (
                <Link key={g.path} to={g.path} className="p-4 bg-white/5 rounded-xl border border-gray-800 hover:border-amber-500/40 transition-colors group">
                  <div className="text-white font-semibold group-hover:text-amber-400 transition-colors">{t(`guide_links.${g.path.slice(1)}.name`)}</div>
                  <div className="text-gray-400 text-sm mt-1">{t(`guide_links.${g.path.slice(1)}.desc`)}</div>
                </Link>
              ))}
            </div>
            <h2 className="text-xl font-bold text-white mt-10">{t('resources.glossary_work')}</h2>
          </section>
        )}

        {/* Items as expandable accordion */}
        <section className="max-w-4xl mx-auto px-4">
          <div className="space-y-4">
            {resource.items.map((item, i) => {
              const expanded = openItem === i;
              return (
                <div key={i} className="bg-white/5 rounded-xl border border-gray-800 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenItem(expanded ? null : i)}
                    aria-expanded={expanded}
                    className="w-full text-left p-6 hover:bg-white/[0.03] transition-colors group flex items-start justify-between gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="text-white font-semibold group-hover:text-amber-400 transition-colors mb-1">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                      {item.date && <p className="text-gray-500 text-xs mt-2">{item.date}</p>}
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 mt-1 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded && item.body && (
                    <div className="px-6 pb-6 border-t border-gray-800 pt-5">
                      <div className="space-y-4 text-gray-300 leading-relaxed">
                        {item.body.map((p, k) => <p key={k}>{p}</p>)}
                      </div>
                      {item.cta && (
                        item.cta.to ? (
                          <Link to={item.cta.to} className="inline-flex items-center gap-2 mt-5 text-amber-400 hover:text-amber-300 font-medium text-sm">
                            {item.cta.label}
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        ) : (
                          <a href={item.cta.href} className="inline-flex items-center gap-2 mt-5 text-amber-400 hover:text-amber-300 font-medium text-sm">
                            {item.cta.label}
                            <ArrowRight className="w-4 h-4" />
                          </a>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('resources.cta.headline')}</h2>
          <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
            {t('resources.cta.button')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ResourcePage;
